import React from "react";

import { DetailDisplay, Table } from "widgets";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";
import { formatPhoneWithOptionalIsd } from "utils/phoneValidation";
import { getOrderShippingAddress } from "utils/orderShipping";

import styles from "./ReviewTransferOrder.module.scss";

const NA_VALUE = "N/A";

const isPresentAddressPart = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (trimmed.toLowerCase() === "none") return false;
  }
  return true;
};

const formatCommaSeparatedAddress = (address, fallbackIsdCode) => {
  if (!address || typeof address !== "object") return "-";
  const phoneNumber =
    address.recipientPhoneNumber ??
    address.maskedPhoneNo ??
    address.masked_phone_no;
  const isdCode = address.isdCode ?? address.isd_code ?? fallbackIsdCode;
  const parts = [
    address.recipientName ?? address.recipient_name,
    address.parentName,
    formatPhoneWithOptionalIsd(phoneNumber, isdCode),
    address.email ?? address.recipientEmail,
    address.addressLine1 ?? address.address_line_1,
    address.addressLine2 ?? address.address_line_2,
    address.street,
    address.city,
    address.district,
    address.state,
    address.postalCode ?? address.postal_code ?? address.pincode,
    address.country,
  ];
  const filtered = parts.filter(isPresentAddressPart);
  return filtered.length ? filtered.join(", ") : "-";
};

const getAdjustedPayment = (amountPaidInPreviousOrder, newOrderAmount) => {
  if (amountPaidInPreviousOrder > newOrderAmount) {
    return {
      adjustedPayment: newOrderAmount,
      remainingPaymentToRefund: amountPaidInPreviousOrder - newOrderAmount,
      remainingPaymentToCollect: 0,
    };
  } else if (amountPaidInPreviousOrder < newOrderAmount) {
    return {
      adjustedPayment: amountPaidInPreviousOrder,
      remainingPaymentToRefund: 0,
      remainingPaymentToCollect: newOrderAmount - amountPaidInPreviousOrder,
    };
  } else {
    return {
      adjustedPayment: newOrderAmount,
      remainingPaymentToRefund: 0,
      remainingPaymentToCollect: 0,
    };
  }
};

const ReviewTransferOrder = (props) => {
  const {
    transferOrderDetails,
    billingAddress,
    shippingAddress,
    useSameAddress,
    hasHardware,
    selectedPackageSubscriptions,
    discountDetails,
    newOrderAmount,
    currencyCode,
    isdCode,
  } = props;

  const amountPaidInPreviousOrder =
    transferOrderDetails && transferOrderDetails.amountPaid;
  const {
    adjustedPayment,
    remainingPaymentToRefund,
    remainingPaymentToCollect,
  } = getAdjustedPayment(amountPaidInPreviousOrder, newOrderAmount);

  const newDiscountDetails = discountDetails
    ? `${discountDetails.coupon_code ? `Coupon Code: ${discountDetails.coupon_code}` : ""}
       ${discountDetails.discount_value ? (discountDetails.coupon_code ? " | " : "") + `Discount Value: ${discountDetails.is_percentage ? discountDetails.discount_value + "%" : formatMonetaryAmount(discountDetails.discount_value, currencyCode)}` : ""}`.trim()
    : "-";

  const oldDiscountDetails = transferOrderDetails.coupon
    ? `${transferOrderDetails.coupon.code ? `Coupon Code: ${transferOrderDetails.coupon.code}` : ""}
       ${transferOrderDetails.coupon.value ? (transferOrderDetails.coupon.code ? " | " : "") + `Discount Value: ${formatMonetaryAmount(transferOrderDetails.coupon.value, currencyCode)}` : ""}`.trim()
    : "-";

  const newPackageNames = selectedPackageSubscriptions
    .map((pkg) => pkg.displayName)
    .join(", ");
  const previousPackageNames = transferOrderDetails.packages
    .map((pkg) => pkg.displayName)
    .join(", ");

  const previousBillingAddress =
    transferOrderDetails.addressDetails?.billingAddress;
  const previousShippingAddress = getOrderShippingAddress(
    transferOrderDetails,
  );

  const formattedNewBillingAddress = formatCommaSeparatedAddress(
    billingAddress,
    isdCode,
  );
  const formattedOldBillingAddress = formatCommaSeparatedAddress(
    previousBillingAddress,
    isdCode,
  );

  const formattedOldShippingAddress = formatCommaSeparatedAddress(
    previousShippingAddress,
    isdCode,
  );
  const formattedNewShippingAddress = hasHardware
    ? formatCommaSeparatedAddress(
        useSameAddress ? billingAddress : shippingAddress,
        isdCode,
      )
    : NA_VALUE;

  const columns = [
    { label: "Field", field: "field", align: "center" },
    { label: "Previous Value", field: "oldValue", align: "center" },
    { label: "New Value", field: "newValue", align: "center" },
  ];

  const rows = [
    {
      field: "Billing Address",
      oldValue: formattedOldBillingAddress,
      newValue: formattedNewBillingAddress,
    },
    {
      field: "Shipping Address",
      oldValue: formattedOldShippingAddress,
      newValue: formattedNewShippingAddress,
    },
    {
      field: "Package",
      oldValue: previousPackageNames,
      newValue: newPackageNames,
    },
    {
      field: "Discount Code",
      oldValue: oldDiscountDetails,
      newValue: newDiscountDetails,
    },
    {
      field: "Order Amount",
      oldValue: formatMonetaryAmount(
        transferOrderDetails.amount,
        currencyCode,
      ),
      newValue: formatMonetaryAmount(newOrderAmount, currencyCode),
    },
  ];

  return (
    <div className={styles.mainContainer}>
      <div className={styles.displayOrderId}>
        <span className={styles.title}>Previous Order Id :</span>
        <span className={styles.value}>{transferOrderDetails.orderId}</span>
      </div>
      <Table columns={columns} rows={rows} />
      <div className={styles.paymentDetails}>
        <DetailDisplay
          title="Adjusted Payment"
          value={formatMonetaryAmount(adjustedPayment, currencyCode)}
        />
        <DetailDisplay
          title="Remaining payment to refund"
          value={formatMonetaryAmount(
            remainingPaymentToRefund,
            currencyCode,
          )}
        />
        <DetailDisplay
          title="Remaining payment to collect"
          value={formatMonetaryAmount(
            remainingPaymentToCollect,
            currencyCode,
          )}
        />
      </div>
    </div>
  );
};

export default ReviewTransferOrder;
